"use client";

import React from "react";
import { MapPin, Mail, Phone, Facebook, Instagram, Linkedin, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function NeemContactOp() {
  return <section data-section-id="26719" id="connect-met-ons" className="text-foreground py-20 !bg-[hsl(20,100%,98%)] md:py-32">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {}
          <div className="space-y-10">
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-bold text-primary font-default tracking-tight">Laten we verbinden</h2>
              <p className="text-muted-foreground text-lg leading-relaxed max-w-md font-default">Klaar om te groeien? Neem contact op voor een vrijblijvend gesprek over je online toekomst.</p>
            </div>

            <div className="space-y-8">
              <div className="space-y-1">
                <h3 className="text-primary font-bold text-lg font-default">Locatie</h3>
                <div className="flex items-start gap-3 text-muted-foreground font-default">
                  <MapPin className="w-5 h-5 mt-1 shrink-0 text-accent-foreground" />
                  <p>Haarlemmermeer based, overal online</p>
                </div>
              </div>

              <div className="space-y-1">
                <h3 className="text-primary font-bold text-lg font-default">E-mail</h3>
                <div className="flex items-center gap-3 text-muted-foreground font-default">
                  <Mail className="w-5 h-5 shrink-0 text-accent-foreground" />
                  <Link to="mailto:info@connectedonline.nl" className="hover:text-accent-foreground transition-colors hover:underline">hello@connectedonline.nl</Link>
                </div>
              </div>

              <div className="space-y-1">
                <h3 className="text-primary font-bold text-lg font-default">Telefoon</h3>
                <div className="flex items-center gap-3 text-muted-foreground font-default">
                  <Phone className="w-5 h-5 shrink-0 text-accent-foreground" />
                  <Link to="tel:+310537901605" className="hover:text-accent-foreground transition-colors hover:underline">+31 (6) 40 25 51 26</Link>
                </div>
              </div>
            </div>

            <div className="pt-4">
              <div className="flex gap-4">
                <Link to="https://instagram.com" newTab className="bg-[#C13584] text-white p-3 rounded-full hover:opacity-80 transition-opacity flex items-center justify-center shadow-sm" aria-label="Instagram">
                  <Instagram className="w-5 h-5" />
                </Link>
                
                <Link to="https://www.linkedin.com/company/connected-online/posts/?feedView=all" newTab className="bg-[#0077B5] text-white p-3 rounded-full hover:opacity-80 transition-opacity flex items-center justify-center shadow-sm" aria-label="LinkedIn">
                  <Linkedin className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>

          {}
          <div className="bg-transparent">
            <FormProvider formKey="contact_form_contact" formId="contact_form_contact" sectionName="contact-section" defaultValues={{
            name: "",
            email: "",
            phone: "",
            subject: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: false
            },
            subject: {
              required: true
            },
            message: {
              required: true
            }
          }} submitText="Verstuur bericht" successMessage="Bedankt voor uw bericht! We nemen binnen 24 uur contact met u op." errorMessage="Er is iets misgegaan. Probeer het later opnieuw." className="w-full">
              <ContactFormContent />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-10 bg-secondary rounded-xl border border-border/50 text-center shadow-lg">
        <h3 className="text-2xl font-bold text-primary mb-4 font-default">Bericht Ontvangen!</h3>
        <p className="text-muted-foreground font-default leading-relaxed">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="name" rules={{
        required: "Naam is verplicht"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-semibold font-default">Naam</FormLabel>
              <FormControl>
                <Input placeholder="Uw volledige naam" {...field} className="border-t-0 border-x-0 border-b border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-accent-foreground bg-transparent shadow-none font-default placeholder:text-muted-foreground/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "E-mailadres is verplicht",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Ongeldig e-mailadres"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-semibold font-default">E-mailadres</FormLabel>
              <FormControl>
                <Input type="email" placeholder="uw@email.nl" {...field} className="border-t-0 border-x-0 border-b border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-accent-foreground bg-transparent shadow-none font-default placeholder:text-muted-foreground/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-semibold font-default">Telefoonnummer</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+31 6 1234 5678" {...field} className="border-t-0 border-x-0 border-b border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-accent-foreground bg-transparent shadow-none font-default placeholder:text-muted-foreground/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subject" rules={{
        required: "Onderwerp is verplicht"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-semibold font-default">Onderwerp</FormLabel>
              <FormControl>
                <Input placeholder="Bijv. AI-Chatbots of Social Media" {...field} className="border-t-0 border-x-0 border-b border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-accent-foreground bg-transparent shadow-none font-default placeholder:text-muted-foreground/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Bericht is verplicht"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary font-semibold font-default">Bericht</FormLabel>
            <FormControl>
              <Textarea placeholder="Hoe kunnen we uw groei versnellen?" {...field} className="min-h-[120px] border-t-0 border-x-0 border-b border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-accent-foreground bg-transparent shadow-none resize-y font-default placeholder:text-muted-foreground/50" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 text-destructive text-sm border border-destructive/20 rounded-lg font-default">
          {submitError}
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[180px] bg-primary hover:bg-primary/90 text-primary-foreground rounded-full h-14 px-10 text-base font-bold cursor-pointer transition-all duration-300 shadow-lg hover:shadow-xl active:scale-95 font-default">
          {isSubmitting ? <>
              <Loader className="mr-2 h-5 w-5 animate-spin" />
              Bezig met verzenden...
            </> : "Verstuur bericht"}
        </Button>
      </div>
    </Form>;
}